import {
  User,
  Wallet,
  IndianRupee,
  BadgePercent,
  Clock3,
  ShieldCheck,
  CheckCircle2,
  HandCoins,
  Briefcase,
  Banknote,
  CreditCard,
  RefreshCcw,
  Users,
  Building2,
  Landmark,
  FileText,
} from "./icons";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

const schemas = [
  websiteSchema(),
  organizationSchema(),
  financialServiceSchema(),
  localBusinessSchema(),
  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Personal Loan",
      path: "/personal-loan",
    },
  ]),
];

/* -------------------------------------------------------------------------- */
/*                                    SEO                                     */
/* -------------------------------------------------------------------------- */

export const seo = {
  title: "Personal Loan | Instant Unsecured Loan | Money Mart",

  description:
    "Get a Personal Loan from leading Banks & NBFCs with competitive interest rates, flexible EMIs and quick approvals. Apply for an unsecured loan for medical emergencies, weddings, travel, education, home renovation and more.",

  keywords:
    "Personal Loan, Instant Personal Loan, Unsecured Loan, Salary Loan, Emergency Loan, Personal Finance, Money Mart",

  canonical: "/personal-loan",

  structuredData: { schemas },
};

/* -------------------------------------------------------------------------- */
/*                                    Hero                                    */
/* -------------------------------------------------------------------------- */

export const hero = {
  badge: "Unsecured Personal Finance",

  title: "Meet Your Financial Needs with an Easy Personal Loan",

  description:
    "Whether you need funds for a medical emergency, wedding, education, travel, home renovation or any personal requirement, Money Mart helps you compare Personal Loan offers from leading Banks & NBFCs with competitive interest rates and quick approvals.",

  highlights: [
    "No Collateral Required",
    "Competitive Interest Rates",
    "Flexible EMI Options",
    "Quick Loan Processing",
  ],

  image: "/images/loans/personal_loan.webp",

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

/* -------------------------------------------------------------------------- */
/*                                  Overview                                  */
/* -------------------------------------------------------------------------- */

export const overview = {
  badge: "Personal Loan",

  title: "Instant Financial Support Without Collateral",

  description:
    "A Personal Loan is an unsecured loan that helps you manage planned or unexpected expenses without pledging any collateral. Loan approval is primarily based on your income, credit profile and repayment capacity.",

  items: [
    {
      icon: HandCoins,
      title: "No Security Required",
      description:
        "Personal Loans are unsecured, so you don't need to mortgage property or pledge any assets.",
    },
    {
      icon: Wallet,
      title: "Flexible Usage",
      description:
        "Use the loan amount for medical expenses, weddings, education, travel, home renovation or other personal needs.",
    },
    {
      icon: Clock3,
      title: "Quick Access to Funds",
      description:
        "Benefit from faster approvals and quick disbursals, subject to lender policies.",
    },
    {
      icon: CreditCard,
      title: "Convenient Repayment",
      description: (
        <>
          Repay your loan through affordable EMIs over flexible repayment
          tenures. <strong> Max tenure: 7 years.</strong>
        </>
      ),
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                  Benefits                                  */
/* -------------------------------------------------------------------------- */

export const benefits = {
  badge: "Benefits",

  title: "Why Choose a Personal Loan?",

  description:
    "Personal Loans offer quick access to funds without collateral, making them an ideal solution for both planned and unexpected financial needs.",

  items: [
    {
      icon: ShieldCheck,
      title: "No Collateral",
      description:
        "Borrow funds without pledging property, gold or any other asset as security.",
    },
    {
      icon: BadgePercent,
      title: "Competitive Interest Rates",
      description:
        "Compare loan offers from multiple Banks & NBFCs to find the most suitable option.",
    },
    {
      icon: Clock3,
      title: "Quick Processing",
      description:
        "Experience faster approvals and hassle-free processing with expert assistance.",
    },
    {
      icon: Wallet,
      title: "Flexible Repayment",
      description:
        "Choose repayment tenures and EMI options that match your financial goals.",
    },
    {
      icon: IndianRupee,
      title: "Multipurpose Loan",
      description:
        "Use the loan for almost any genuine personal financial requirement without usage restrictions from most lenders.",
    },
    {
      icon: CheckCircle2,
      title: "Minimal Documentation",
      description:
        "Most lenders require only basic KYC, income proof and employment or business documents.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export const types = {
  badge: "Personal Loan Solutions",

  title: "Personal Loan Solutions We Help Arrange",

  description:
    "Money Mart helps customers compare various Personal Loan options from leading Banks & NBFCs based on their financial requirements and eligibility.",

  items: [
    {
      icon: User,
      title: "Personal Loan for Salaried Individuals",
      description:
        "Unsecured loans designed for salaried professionals with stable income and repayment capacity.",
    },
    {
      icon: Briefcase,
      title: "Personal Loan for Self-Employed",
      description:
        "Financing solutions for self-employed professionals and business owners based on income eligibility.",
    },
    {
      icon: RefreshCcw,
      title: "Personal Loan Balance Transfer",
      description:
        "Transfer your existing Personal Loan to another lender for better interest rates or improved repayment terms.",
    },
    {
      icon: Banknote,
      title: "Top-Up Personal Loan",
      description:
        "Eligible borrowers may obtain additional funding over their existing Personal Loan, subject to lender policies.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                Eligibility                                 */
/* -------------------------------------------------------------------------- */

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your Personal Loan Eligibility",

  description:
    "Personal Loan eligibility depends on your income, repayment capacity, employment profile, credit history, and the lender's internal credit policy.",

  requirements: [
    {
      icon: User,
      title: "Applicant",
      value:
        "Salaried Individuals, Self-Employed Professionals & Business Owners",
    },
    {
      icon: Briefcase,
      title: "Employment",
      value: "Stable Employment or Established Business (as per lender norms)",
    },
    {
      icon: IndianRupee,
      title: "Income",
      value: "Regular & Verifiable Source of Income",
    },
    {
      icon: CreditCard,
      title: "Credit Profile",
      value: "Good Credit History and Repayment Record",
    },
  ],

  approvalFactors: [
    "Monthly income and repayment capacity",
    "Credit score and repayment history",
    "Employment or business stability",
    "Existing EMIs and financial obligations",
    "Requested loan amount and tenure",
    "Lender's internal eligibility criteria",
  ],

  cta: {
    title: "Want to know your loan eligibility?",

    description:
      "Our experts will evaluate your profile and help you compare Personal Loan offers from leading Banks & NBFCs.",

    buttonText: "Check Eligibility Now",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Documents                                  */
/* -------------------------------------------------------------------------- */

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for a Personal Loan",

  description:
    "The exact documentation may vary depending on the lender and applicant profile. These are the documents commonly required for most Personal Loan applications.",

  groups: [
    {
      icon: Users,
      title: "Documents Required for All Applicants",

      documents: [
        "Passport Size Photograph",
        "PAN Card",
        "Aadhaar Card",
        "Address Proof",
      ],
    },
    {
      icon: Briefcase,
      title: "Income Documents (Salaried)",

      documents: [
        "Latest Salary Slips",
        "Salary Account Bank Statements",
        "Form 16 (if required)",
        "Employment ID / Offer Letter (if applicable)",
      ],
    },
    {
      icon: Building2,
      title: "Income Documents (Self-Employed)",

      documents: [
        "Last 2–3 Years Income Tax Returns",
        "Balance Sheet & Profit & Loss Statement",
        "Business Proof",
        "Business Bank Statements",
      ],
    },
    {
      icon: Landmark,
      title: "Banking Documents",

      documents: [
        "Last 6 Months Bank Statements",
        "Existing Loan Statements (if applicable)",
      ],
    },
    {
      icon: FileText,
      title: "Additional Supporting Documents",

      documents: [
        "Employment Proof (if required)",
        "Any Additional Documents Requested by the Lender",
      ],
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Process                                  */
/* -------------------------------------------------------------------------- */

export const loanProcess = {
  ...process,

  title: "Get Your Personal Loan in 6 Simple Steps",

  description:
    "From checking your eligibility and verifying documents to loan approval and disbursal, Money Mart supports you throughout the Personal Loan journey.",
};

/* -------------------------------------------------------------------------- */
/*                              Why Money Mart                                */
/* -------------------------------------------------------------------------- */

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Choose Money Mart for Your Personal Loan?",

  description:
    "We help you compare Personal Loan offers from leading Banks & NBFCs, enabling you to secure competitive interest rates, flexible repayment options, and a smooth borrowing experience.",

  items: whyMoneyMartItems,
};

/* -------------------------------------------------------------------------- */
/*                                     FAQ                                    */
/* -------------------------------------------------------------------------- */

export const faq = {
  badge: "Frequently Asked Questions",

  title: "Personal Loan FAQs",

  description:
    "Find answers to the most commonly asked questions about Personal Loans.",

  items: [
    {
      question: "Do I need collateral to get a Personal Loan?",

      answer:
        "No. Personal Loans are unsecured loans, which means you generally don't need to pledge property, gold, or any other asset as collateral.",
    },
    {
      question: "What can I use a Personal Loan for?",

      answer:
        "A Personal Loan can be used for various genuine personal needs such as medical emergencies, weddings, education, travel, home renovation, debt consolidation, or other personal expenses.",
    },
    {
      question: "Can I transfer my existing Personal Loan to another bank?",

      answer:
        "Yes. Eligible borrowers can opt for a Personal Loan Balance Transfer to another lender for better interest rates, improved repayment terms, or additional benefits, subject to lender approval.",
    },
    {
      question: "Can I get a top-up on my existing Personal Loan?",

      answer:
        "Many lenders offer Top-Up Personal Loans to eligible borrowers with a good repayment history, subject to their internal policies.",
    },
    {
      question: "What factors affect Personal Loan approval?",

      answer:
        "Approval depends on factors such as your income, repayment capacity, credit score, employment or business stability, existing financial obligations, and the lender's internal credit assessment.",
    },
    {
      question: "How long does Personal Loan approval take?",

      answer:
        "Approval timelines vary by lender and documentation. Applications with complete documentation generally receive faster processing and disbursal.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                     CTA                                    */
/* -------------------------------------------------------------------------- */

export const cta = {
  badge: "Need Funds Quickly?",

  title: "Compare Personal Loan Offers from Leading Banks & NBFCs",

  description:
    "Let Money Mart help you find the right Personal Loan with competitive interest rates, flexible repayment options, and expert assistance throughout the application process.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for Personal Loan",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Lead Form                                  */
/* -------------------------------------------------------------------------- */

export const leadForm = {
  badge: "Free Personal Loan Consultation",

  title: "Speak to a Personal Loan Expert",

  description:
    "Our advisors will compare Personal Loan offers from multiple Banks & NBFCs and help you choose the most suitable financing solution based on your requirements.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our Personal Loan expert will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Employment Type",
      income: "Monthly Income",
      loanAmount: "Required Loan Amount",
    },

    incomeField: "monthlyIncome",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-25k",
        label: "Below ₹25,000",
      },
      {
        value: "25k-50k",
        label: "₹25,000 – ₹50,000",
      },
      {
        value: "50k-1L",
        label: "₹50,000 – ₹1,00,000",
      },
      {
        value: "1L-2L",
        label: "₹1,00,000 – ₹2,00,000",
      },
      {
        value: "2L-plus",
        label: "Above ₹2,00,000",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your Personal Loan enquiry. No spam. No hidden charges.",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Export                                    */
/* -------------------------------------------------------------------------- */

const personalLoan = {
  seo,
  hero,
  overview,
  benefits,
  types,
  eligibility,
  documents,
  process: loanProcess,
  whyMoneyMart,
  faq,
  cta,
  leadForm,
};

export default personalLoan;
